/**
 * Pontuação de saúde da aeronave (0–100) com semáforo.
 *
 * Calcula-se no cliente com o que já está carregado (estado operacional, seguro e resumo das manutenções) e
 * devolve também os motivos (`factors`), que aparecem no balão ao passar o rato por cima do número.
 * O estado operacional calculado no servidor tem a palavra final: não operacional nunca é "ok".
 */

function _daysUntil(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate + "T00:00:00");
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}

// ─── Health Score 0–100 ───────────────────────────────────────────────

/**
 * Calcula um score 0–100 com tier semáforo.
 *
 * Cada penalidade indica:
 *   - "critical": expirou / ultrapassou limite
 *   - "warn":     <30d ou <2h restantes
 *   - "info":     <90d ou <5h restantes
 *
 * Reasons (factors) ficam disponíveis para tooltip / debug.
 */
export function computeHealthScore({ aircraft, insurance, maintSummary }) {
  let score = 100;
  const factors = [];

  // Status override: retirada/manutencao → always grounded
  const acStatus = aircraft?.status;
  if (acStatus === "retirada") {
    return {
      score: 0,
      tier: "grounded",
      factors: [{ severity: "critical", text: "Aeronave retirada de serviço" }],
    };
  }
  if (acStatus === "manutencao") {
    return {
      score: 0,
      tier: "grounded",
      factors: [{ severity: "critical", text: "Aeronave em manutenção" }],
    };
  }

  // Docs (navegabilidade, lic. rádio)
  const docs = aircraft?.operational_state?.criteria?.documents || {};
  for (const [key, label] of [
    ["airworthiness_valid_until", "Navegabilidade"],
    ["radio_license_valid_until", "Lic. Rádio"],
  ]) {
    const d = docs[key];
    if (!d) continue;
    if (d.missing) {
      score -= 25;
      factors.push({ severity: "critical", text: `${label} em falta` });
    } else if (d.days_remaining != null && d.days_remaining < 0) {
      score -= 30;
      factors.push({ severity: "critical", text: `${label} expirou há ${-d.days_remaining}d` });
    } else if (d.days_remaining != null && d.days_remaining < 30) {
      score -= 15;
      factors.push({ severity: "warn", text: `${label} expira em ${d.days_remaining}d` });
    } else if (d.days_remaining != null && d.days_remaining < 90) {
      score -= 5;
      factors.push({ severity: "info", text: `${label} expira em ${d.days_remaining}d` });
    }
  }

  // Seguro
  if (!insurance) {
    score -= 25;
    factors.push({ severity: "critical", text: "Sem seguro registado" });
  } else {
    const days = _daysUntil(insurance.policy_end);
    if (days != null && days < 0) {
      score -= 30;
      factors.push({ severity: "critical", text: `Seguro expirou há ${-days}d` });
    } else if (days != null && days < 30) {
      score -= 15;
      factors.push({ severity: "warn", text: `Seguro expira em ${days}d` });
    } else if (days != null && days < 90) {
      score -= 5;
      factors.push({ severity: "info", text: `Seguro expira em ${days}d` });
    }
  }

  // Manutenções (critical = fora de tolerância; warn inclui as vencidas dentro da tolerância)
  const counts = maintSummary?.counts || {};
  const inTol = counts.in_tolerance || 0;
  if ((counts.critical || 0) > 0) {
    score -= 20 * Math.min(counts.critical, 2);
    factors.push({
      severity: "critical",
      text: `${counts.critical} manutenção(ões) fora de tolerância`,
    });
  }
  if (inTol > 0) {
    score -= 8 * Math.min(inTol, 2);
    factors.push({
      severity: "warn",
      text: `${inTol} manutenção(ões) vencida(s), dentro da tolerância`,
    });
  }
  const approaching = Math.max(0, (counts.warn || 0) - inTol);
  if (approaching > 0) {
    score -= 8 * Math.min(approaching, 2);
    factors.push({
      severity: "warn",
      text: `${approaching} manutenção(ões) com atenção`,
    });
  }
  if ((counts.info || 0) > 0) {
    score -= 3;
    factors.push({
      severity: "info",
      text: `${counts.info} manutenção(ões) a aproximar-se do limite`,
    });
  }

  score = Math.max(0, Math.min(100, Math.round(score)));

  let tier;
  if (score >= 85) tier = "ok";
  else if (score >= 60) tier = "risk";
  else tier = "grounded";

  // O estado operacional calculado no servidor (documentos + revisões + manutenções, com tolerância) é
  // a palavra final: não operacional → "grounded"; operacional com avisos → nunca "ok".
  const op = aircraft?.operational_state;
  if (op && op.operacional === false) {
    tier = "grounded";
    score = Math.min(score, 59);
    if (!factors.some((f) => f.severity === "critical")) {
      factors.unshift({ severity: "critical", text: op.bloqueios?.[0] || "Aeronave não operacional" });
    }
  } else if (op && (op.avisos || []).length > 0 && tier === "ok") {
    tier = "risk";
    score = Math.min(score, 84);
    factors.push({ severity: "warn", text: op.avisos[0] });
  }

  return { score, tier, factors };
}
